'use client';

import { useEffect, useState } from 'react';
import { useConvex, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import Image from '@/components/ui/CspImage';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { Search, ArrowRight, Clock, Sparkles, ChevronRight, Menu, X } from 'lucide-react';
import BlogCard from '@/components/landing/BlogCard';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import NewsletterSignup from '@/components/newsletter/NewsletterSignup';
import SiteFooter from '@/components/landing/SiteFooter';
import WaitlistModal from '@/components/WaitlistModal';

// Category color mapping - using landing page primary color
const categoryColors: Record<string, { bg: string; text: string }> = {
  'fashion-trends': { bg: 'bg-primary/10', text: 'text-primary' },
  'product-updates': { bg: 'bg-blue-500/10', text: 'text-blue-600' },
  'tutorials': { bg: 'bg-emerald-500/10', text: 'text-emerald-700' },
  'guides': { bg: 'bg-emerald-500/10', text: 'text-emerald-700' },
  'industry-insights': { bg: 'bg-blue-500/10', text: 'text-blue-600' },
  'company-news': { bg: 'bg-violet-500/10', text: 'text-violet-600' },
  'ai-insights': { bg: 'bg-amber-500/10', text: 'text-amber-600' },
};

interface BlogListClientProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialPosts?: Record<string, any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialFeatured?: Record<string, any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialCategories?: Record<string, any>[];
}

export default function BlogListClient({ initialPosts, initialFeatured, initialCategories }: BlogListClientProps) {
  const t = useTranslations('blog');
  const tNav = useTranslations('nav');
  const locale = useLocale();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadedPosts, setLoadedPosts] = useState<Record<string, any>[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [cursorInvalid, setCursorInvalid] = useState(false);
  const convex = useConvex();
  const waitlistMode = useQuery(api.systemSettings.getWaitlistMode, {}) ?? false;
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const categoriesLive = useQuery(api.blogCategories.getActiveCategories);
  const categories = categoriesLive ?? initialCategories;
  const postsResultLive = useQuery(api.blogPosts.getPublishedPosts, {
    limit: 20,
    categorySlug: selectedCategory !== 'all' ? selectedCategory : undefined,
    searchQuery: searchQuery || undefined,
  });
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!postsResultLive) return;
    setLoadedPosts(postsResultLive.posts ?? []);
    setNextCursor(postsResultLive.nextCursor);
    setLoadMoreError(null);
    setCursorInvalid(false);
  }, [postsResultLive]);

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    setCursorInvalid(false);
    try {
      const page = await convex.query(api.blogPosts.getPublishedPosts, {
        limit: 20,
        categorySlug: selectedCategory !== 'all' ? selectedCategory : undefined,
        searchQuery: searchQuery || undefined,
        cursor: nextCursor,
      });
      setLoadedPosts((previous) => {
        const existing = new Set(previous.map((post) => post._id));
        return [...previous, ...page.posts.filter((post: any) => !existing.has(post._id))];
      });
      setNextCursor(page.nextCursor);
    } catch (error) {
      const invalid = error instanceof Error && error.message.includes('Invalid blog pagination cursor');
      setCursorInvalid(invalid);
      setLoadMoreError(invalid ? t('cursorExpired') : t('loadMoreError'));
    } finally {
      setIsLoadingMore(false);
    }
  };

  const postsResult =
    postsResultLive ??
    (selectedCategory === 'all' && !searchQuery ? initialPosts : undefined);
  const featuredPostsLive = useQuery(api.blogPosts.getFeaturedPosts, { limit: 1 });
  const featuredPosts = featuredPostsLive ?? initialFeatured;

  const posts = loadedPosts.length > 0 ? loadedPosts : postsResult?.posts || [];
  const heroPost = featuredPosts?.[0];
  const isZh = locale === 'zh-TW';
  const isLoadingPosts = !postsResultLive && !postsResult;

  const getDisplayTitle = (post: any) => isZh && post.titleZh ? post.titleZh : post.title;
  const getDisplayExcerpt = (post: any) => isZh && post.excerptZh ? post.excerptZh : post.excerpt;

  return (
    <div className="landing-theme min-h-screen bg-background font-body">
      {/* Navigation - matching landing page */}
      <nav aria-label="Main navigation" className="fixed top-0 w-full z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center">
              <Image
                src="/StyTrix_brand.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="block dark:hidden w-auto h-[2.25rem]"
                priority
              />
              <Image
                src="/StyTrix_brand_dark.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="hidden dark:block w-auto h-[2.25rem]"
                priority
              />
            </Link>

            {/* Navigation Links - matching landing page */}
            <div className="hidden md:flex items-center gap-8">
              <Link href="/documentation" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {tNav('documentation')}
              </Link>
              <Link href="/#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {tNav('pricing')}
              </Link>
              <Link href="/blog" className="text-sm font-medium text-foreground transition-colors">
                {tNav('blog')}
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher className="hidden md:flex" />
              {waitlistMode ? (
                <button
                  onClick={() => setIsWaitlistOpen(true)}
                  className="hidden sm:block px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg whitespace-nowrap"
                >
                  {tNav('joinWaitlist')}
                </button>
              ) : (
                <a
                  href="/sign-in"
                  className="hidden sm:block px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg"
                >
                  {tNav('signIn')}
                </a>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2.5 rounded-lg hover:bg-secondary transition-colors"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-lg border-b border-border shadow-elevated-lg transition-all duration-300 ${
            isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
        >
          <div className="px-6 py-6 space-y-4">
            <Link
              href="/documentation"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border"
            >
              {tNav('documentation')}
            </Link>
            <Link
              href="/#pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border"
            >
              {tNav('pricing')}
            </Link>
            <Link
              href="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border"
            >
              {tNav('blog')}
            </Link>
            {waitlistMode ? (
              <button
                onClick={() => { setIsWaitlistOpen(true); setIsMobileMenuOpen(false); }}
                className="w-full mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all"
              >
                {tNav('joinWaitlist')}
              </button>
            ) : (
              <a
                href="/sign-in"
                className="block w-full mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all text-center"
              >
                {tNav('signIn')}
              </a>
            )}
            <div className="flex items-center justify-center pt-4 border-t border-border">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </nav>
      <main id="main-content">

      {/* Hero Section with Featured Post */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-secondary to-background">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-muted-foreground mb-8 overflow-hidden">
            <Link href="/" className="hover:text-primary transition-colors whitespace-nowrap shrink-0">
              {t('home')}
            </Link>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <span className="text-foreground font-medium whitespace-nowrap">{tNav('blog')}</span>
          </nav>

          {/* Header - matching landing page typography */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary tracking-wide">{t('badge')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
              {t('blogTitle')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto whitespace-pre-line [word-break:auto-phrase]">
              {t('blogDescription')}
            </p>
          </div>

          {/* Featured Post */}
          {heroPost && (
            <Link href={`/blog/${heroPost.slug}`} className="block group">
              <article className="relative bg-card rounded-3xl overflow-hidden border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-elevated-lg hover:-translate-y-1">
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Image */}
                  <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden">
                    {heroPost.r2FeaturedImageUrl || heroPost.featuredImageUrl ? (
                      <Image
                        src={heroPost.r2FeaturedImageUrl || heroPost.featuredImageUrl || ''}
                        alt={heroPost.featuredImageAlt || getDisplayTitle(heroPost)}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        priority
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                        <span className="text-primary/40 text-9xl font-display">{getDisplayTitle(heroPost).charAt(0)}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent lg:hidden" />
                  </div>

                  {/* Content */}
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                          categoryColors[heroPost.categorySlug]?.bg || 'bg-primary/10'
                        } ${categoryColors[heroPost.categorySlug]?.text || 'text-primary'}`}
                      >
                        {heroPost.category?.name || heroPost.categorySlug}
                      </span>
                      {heroPost.readingTimeMinutes && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {t('minRead', { minutes: heroPost.readingTimeMinutes })}
                        </span>
                      )}
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors tracking-tight">
                      {getDisplayTitle(heroPost)}
                    </h2>
                    {getDisplayExcerpt(heroPost) && (
                      <p className="text-muted-foreground text-lg leading-relaxed mb-6 line-clamp-3">
                        {getDisplayExcerpt(heroPost)}
                      </p>
                    )}
                    <div className="flex items-center text-primary font-medium group-hover:gap-1 transition-all duration-300">
                      <span>{t('readArticle')}</span>
                      <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-2" />
                    </div>
                  </div>
                </div>
              </article>
            </Link>
          )}
        </div>
      </section>

      {/* Filters & Posts */}
      <section className="py-20 px-6 bg-background">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-12">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-border bg-card rounded-xl focus:ring-2 focus:ring-primary/30 focus:border-primary focus:shadow-[0_0_0_4px_hsl(var(--primary)/0.1)] text-foreground placeholder:text-muted-foreground transition-all duration-300"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === 'all'
                    ? 'bg-primary text-white shadow-lg shadow-primary/25 scale-[1.02]'
                    : 'bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground hover:-translate-y-[1px] hover:shadow-sm'
                }`}
              >
                {t('allCategories')}
              </button>
              {categories?.map((category) => (
                <button
                  key={category._id}
                  onClick={() => setSelectedCategory(category.slug)}
                  className={`px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category.slug
                      ? 'bg-primary text-white shadow-lg shadow-primary/25 scale-[1.02]'
                      : 'bg-card border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground hover:-translate-y-[1px] hover:shadow-sm'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none md:hidden" />
            </div>
          </div>

          {/* Posts Grid */}
          {isLoadingPosts ? (
            <div className="py-16 text-center text-muted-foreground">{t('loading')}</div>
          ) : posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post: any) => (
                <BlogCard
                  key={post._id}
                  title={post.title}
                  titleZh={post.titleZh}
                  slug={post.slug}
                  excerpt={post.excerpt}
                  excerptZh={post.excerptZh}
                  featuredImageUrl={post.r2FeaturedImageUrl || post.featuredImageUrl}
                  featuredImageAlt={post.featuredImageAlt}
                  categoryName={post.category?.name || post.categorySlug}
                  categorySlug={post.categorySlug}
                  categoryColor={post.category?.color}
                  readingTimeMinutes={post.readingTimeMinutes}
                  publishedAt={post.publishedAt}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">{t('noArticles')}</p>
              <p className="text-muted-foreground text-sm mt-2">
                {searchQuery ? t('noArticlesSearch') : t('noArticlesEmpty')}
              </p>
            </div>
          )}
          {loadMoreError && (
            <div className="mt-8 flex flex-col items-center gap-3 text-center" role="alert">
              <p className="text-sm text-destructive">{loadMoreError}</p>
              <button
                type="button"
                onClick={cursorInvalid
                  ? () => {
                      setLoadedPosts(postsResultLive?.posts ?? []);
                      setNextCursor(postsResultLive?.nextCursor ?? null);
                      setLoadMoreError(null);
                      setCursorInvalid(false);
                    }
                  : loadMore}
                className="text-sm font-medium text-primary underline"
              >
                {cursorInvalid ? t('reload') : t('retry')}
              </button>
            </div>
          )}
          {nextCursor && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isLoadingMore}
                className="rounded-lg border border-border px-6 py-3 text-sm font-medium text-foreground hover:border-primary hover:text-primary disabled:cursor-wait disabled:opacity-60"
              >
                {isLoadingMore ? t('loading') : t('loadMore')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-12 px-6 bg-background">
        <div className="max-w-3xl mx-auto">
          <NewsletterSignup variant="card" source="blog-list" />
        </div>
      </section>
      </main>

      <SiteFooter />

      {/* Waitlist Modal */}
      <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} />
    </div>
  );
}
